import {Table, Spinner} from 'react-bootstrap';
import React, {useEffect, useState} from 'react';
import {FaCaretDown, FaCaretRight} from 'react-icons/fa6';

import useLoader from 'hooks/useLoader';
import {
    WorkflowUsageRate,
    WorkflowUsageRateDetail,
} from '../WorkflowUsageRate.viewmodel';

import {convertToNumberWithComma} from '../../../../modules/Common';

type Props = {
    selectedTab: number;
    workflowUsageRateDetails?: WorkflowUsageRateDetail[];
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDate(dateString: string) {
    return `${dateString.slice(6, 8)}.${dateString.slice(4, 6)}.${dateString.slice(0, 4)}`;
}

function getMonthYearLabel(dateString: string) {
    if (!dateString || dateString.length < 6) return 'Invalid Date';
    
    const monthIndex = parseInt(dateString.slice(4, 6), 10) - 1;
    if (monthIndex < 0 || monthIndex > 11) return 'Invalid Date';
    
    const year = dateString.slice(0, 4);
    
    return `${MONTH_NAMES[monthIndex]} ${year}`;
}

function getDaysInMonth(year: number, month: number) {
    const days = new Date(year, month, 0).getDate();
    return Array.from({length: days}, (_, i) => (i + 1).toString().padStart(2, '0'));
}

function transformData(usageRateDetails: WorkflowUsageRateDetail[]) {
    // console.log('rawdata', usageRateDetails);
    const groupedData: Record<string, Record<string, Record<string, Record<string, number>>>> = {};

    for (const detail of usageRateDetails) {
        const day = detail.datex.slice(6, 8); 
        const monthLabel = getMonthYearLabel(detail.datex); 
        const companyName = detail.name;
        const workflowType = detail.wfType;

        const instanceCount = parseInt(detail.countInstance);
        const callbackCount = parseInt(detail.countCallback);

        const instanceKey = `${workflowType}_Instance`;
        const callbackKey = `${workflowType}_Callback`;

        const keysToUpdate: [string, number][] = [
            ['Total_Instance', instanceCount],
            ['Total_Callback', callbackCount],
            [instanceKey, instanceCount],
            [callbackKey, callbackCount],
        ];

        if (!groupedData[monthLabel]) groupedData[monthLabel] = {};
        if (!groupedData[monthLabel][companyName]) groupedData[monthLabel][companyName] = {};

        // Update each key
        for (const [key, value] of keysToUpdate) {
            if (!groupedData[monthLabel][companyName][key]) {
                groupedData[monthLabel][companyName][key] = {};
            }

            // Initialize day value to 0 if not present, then add current value
            groupedData[monthLabel][companyName][key][day] =
                (groupedData[monthLabel][companyName][key][day] || 0) + value;
        }
    }

    // console.log('groupedData', groupedData);
    return groupedData;
}

export default function GroupUsageRateTable({selectedTab, workflowUsageRateDetails}: Props) {
    const loader = useLoader();

    const [groupedData, setGroupedData] = useState<Record<string, Record<string, Record<string, Record<string, number>>>>>()
    const [dayList, setDayList] = useState<string[]>([]);
    const [expandedMonthLabels, setExpandedMonthLabels] = useState<string[]>([]);
    const [expandedCompanyKeys, setExpandedCompanyKeys] = useState<string[]>([]);

    useEffect(() => {
        loader.show();

        if (workflowUsageRateDetails && workflowUsageRateDetails.length > 0) {
            // set column
            // const year = parseInt(selectedMonthYear.slice(0, 4));
            // const month = parseInt(selectedMonthYear.slice(4, 6));
            // const days = getDaysInMonth(year, month);
            // setDayList(days); 

            // fixed as 31 days valueay with 31 days
            const days = Array.from({length: 31}, (_, index) => (index + 1).toString().padStart(2, '0'));


            setDayList(days);

            // grouped data and set toggle keys
            const grouped = transformData(workflowUsageRateDetails);
            const companyGroupedKeys = Object.keys(grouped)
                .map((month) => {
                    return Object.keys(grouped[month as keyof typeof grouped])
                        .map(company => `${month}-${company}`);
                })
                .reduce((acc, curr) => acc.concat(curr), []);

            setGroupedData(grouped);
            setExpandedMonthLabels(Object.keys(grouped));
            setExpandedCompanyKeys(companyGroupedKeys);
        } else {
            // clear state
            setGroupedData(undefined);
            setExpandedMonthLabels([]);
            setExpandedCompanyKeys([])
        }

        loader.hide();
    }, [workflowUsageRateDetails]);

    const valueType = selectedTab === 0 ? 'Instance' : 'Callback';

    const toggleMonth = (monthLabel: string) => {
        setExpandedMonthLabels(prev =>
            prev.includes(monthLabel) ? prev.filter(m => m !== monthLabel) : [...prev, monthLabel]
        );
    };

    const toggleCompany = (companyKey: string) => {
        setExpandedCompanyKeys(prev =>
            prev.includes(companyKey) ? prev.filter(c => c !== companyKey) : [...prev, companyKey]
        );
    };

    return (
        <div className="overflow-auto max-h-[400px] max-w-full border rounded">
            <Table className="min-w-max table-bordered align-middle" size="sm">
                <thead className="sticky top-0 z-30">
                <tr className="table-active thead-light">
                    <th className="sticky left-0 z-30 w-[120px]">Month</th>
                    <th className="sticky left-[120px] z-20 w-[180px]">Company</th>
                    <th className="sticky left-[300px] z-10 w-[200px]">Workflow Type</th>
                    {dayList.map(day => (
                        <th key={day} className="text-center">{day}</th>
                    ))}
                    <th>Sub Total</th>
                </tr>
                </thead>

                {loader.isLoading && (
                    <tbody>
                    <tr>
                        <td className="text-center text-black bg-[#d1fafd]" colSpan={99}>
                            <Spinner animation="border" size="sm"/> Loading...
                        </td>
                    </tr>
                    </tbody>
                )}

                {!loader.isLoading && (!groupedData || Object.keys(groupedData).length === 0) && (
                    <tbody>
                    <tr>
                        <td className="text-center text-black bg-[#b7b7a4]" colSpan={99}>
                            No Data...
                        </td>
                    </tr>
                    </tbody>
                )}

                <tbody>
                {groupedData && Object.entries(groupedData).map(([monthLabel, companyGroup]) => {
                    const [monthName, yearStr] = monthLabel.split(' ');
                    const monthIndex = MONTH_NAMES.indexOf(monthName) + 1;
                    const daysInMonth = getDaysInMonth(parseInt(yearStr), monthIndex);


                    const monthSubTotal = dayList.reduce((total, day) => {
                        return total + Object.values(companyGroup).reduce((sum, companyData) => {
                            return sum + (companyData[`Total_${valueType}`]?.[day] || 0);
                        }, 0);
                    }, 0);

                    return (
                        <React.Fragment key={monthLabel}>
                            <tr className="bg-[#FABD02]">
                                <td
                                    className="sticky left-0 z-20 bg-[#FABD02] w-[120px] border px-2 py-1 cursor-pointer font-bold"
                                    onClick={() => toggleMonth(monthLabel)}
                                >
                                    {expandedMonthLabels.includes(monthLabel) ? <FaCaretDown/> :
                                        <FaCaretRight/>} {monthLabel}
                                </td>
                                <td className="sticky left-[120px] z-10 bg-[#FABD02] w-[180px] border px-2 py-1"></td>
                                <td className="sticky left-[300px] z-0 bg-[#FABD02] w-[200px] border px-2 py-1 font-bold">Total</td>
                                {dayList.map(day => (
                                    <td key={day} className="border px-2 py-1 font-bold text-center">
                                        {convertToNumberWithComma(
                                            Object.values(companyGroup).reduce((sum, companyData) => {
                                                return sum + (companyData[`Total_${valueType}`]?.[day] || 0);
                                            }, 0)
                                        )}
                                    </td>
                                ))}
                                <td className="bg-[#FABD02] border px-2 py-1 font-bold text-right">
                                    {convertToNumberWithComma(monthSubTotal)}
                                </td>
                            </tr>

                            {expandedMonthLabels.includes(monthLabel) &&
                                Object.entries(companyGroup).map(([companyName, workflowTypeGroup]) => {
                                    const companyKey = `${monthLabel}-${companyName}`;
                                    const companySubTotal = dayList.reduce((sum, day) => {
                                        return sum + (workflowTypeGroup[`Total_${valueType}`]?.[day] || 0);
                                    }, 0);

                                    return (
                                        <React.Fragment key={companyKey}>
                                            <tr className="bg-gray-200">
                                                <td className="sticky left-0 bg-gray-200 w-[120px] z-20 border px-2 py-1"></td>
                                                <td
                                                    className="sticky left-[120px] bg-gray-200 w-[180px] z-10 border px-2 py-1 cursor-pointer font-semibold"
                                                    onClick={() => toggleCompany(companyKey)}
                                                >
                                                    {expandedCompanyKeys.includes(companyKey) ? <FaCaretDown/> :
                                                        <FaCaretRight/>} {companyName}
                                                </td>
                                                <td className="sticky left-[300px] bg-gray-200 w-[200px] z-0 border px-2 py-1 font-semibold">Total</td>
                                                {dayList.map(day => (
                                                    <td key={day}
                                                        className="border px-2 py-1 font-semibold text-center">
                                                        {convertToNumberWithComma(workflowTypeGroup[`Total_${valueType}`]?.[day] || 0)}
                                                    </td>
                                                ))}
                                                <td className="bg-gray-200 border px-2 py-1 font-semibold text-right">
                                                    {convertToNumberWithComma(companySubTotal)}
                                                </td>
                                            </tr>

                                            {expandedCompanyKeys.includes(companyKey) &&
                                                Object.entries(workflowTypeGroup)
                                                    .filter(([workflowTypeKey]) => workflowTypeKey.endsWith(`_${valueType}`) && workflowTypeKey !== `Total_${valueType}`)
                                                    .map(([workflowTypeKey, valuesByDay]) => {
                                                        const workflowSubTotal = dayList.reduce((sum, day) => sum + (valuesByDay[day] || 0), 0);

                                                        return (
                                                            <tr key={`${companyKey}-${workflowTypeKey}`}>
                                                                <td className="sticky left-0 w-[120px] bg-white z-20 border px-2 py-1"></td>
                                                                <td className="sticky left-[120px] w-[180px] bg-white z-10 border px-2 py-1"></td>
                                                                <td className="sticky left-[300px] w-[200px] bg-white z-0 border px-2 py-1">{workflowTypeKey}</td>
                                                                {/*{dayList.map(day => (*/}
                                                                {/*    <td key={day}*/}
                                                                {/*        className="bg-white border px-2 py-1 text-right">*/}
                                                                {/*        {valuesByDay[day] || 0}*/}
                                                                {/*    </td>*/}
                                                                {/*))}*/}

                                                                {dayList.map(day => {
                                                                    const isValidDay = daysInMonth.includes(day);
                                                                    const value = valuesByDay[day] || 0;

                                                                    return (
                                                                        <td key={day}
                                                                            className={`border px-2 py-1 text-center ${isValidDay ? 'bg-white' : 'bg-gray-100 text-white'}`}
                                                                        >
                                                                            {isValidDay ? convertToNumberWithComma(value) : ''}
                                                                        </td>
                                                                    );
                                                                })}

                                                                <td className="bg-white border px-2 py-1 text-right">
                                                                    {convertToNumberWithComma(workflowSubTotal)}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                        </React.Fragment>
                                    );
                                })}
                        </React.Fragment>
                    );
                })}
                </tbody>
            </Table>
        </div>
    );
}